import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../dashboard/components/Sidebar';

const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [view, setView] = useState<'month' | 'week' | 'day'>('month');
  const [showNewEventModal, setShowNewEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [searchParams] = useSearchParams();
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: '',
    time: '',
    duration: 60,
    type: 'meeting',
    client: '',
    description: ''
  });

  const [events, setEvents] = useState([
    {
      id: 1,
      title: 'Project Kickoff Meeting',
      date: new Date(2024, 11, 15, 10, 0),
      duration: 60,
      type: 'meeting',
      client: 'Acme Corp'
    },
    {
      id: 2,
      title: 'Site Inspection',
      date: new Date(2024, 11, 18, 14, 0),
      duration: 120,
      type: 'inspection',
      client: 'Tech Solutions'
    },
    {
      id: 3,
      title: 'Design Review',
      date: new Date(2024, 11, 20, 9, 0),
      duration: 90,
      type: 'review',
      client: 'Creative Studio'
    },
    {
      id: 4,
      title: 'Client Presentation',
      date: new Date(2024, 11, 22, 15, 0),
      duration: 45,
      type: 'presentation',
      client: 'Global Industries'
    },
    {
      id: 5,
      title: 'Team Standup',
      date: new Date(2024, 11, 16, 9, 0),
      duration: 30,
      type: 'meeting',
      client: 'Internal'
    },
    {
      id: 6,
      title: 'Budget Review',
      date: new Date(2024, 11, 19, 11, 0),
      duration: 60,
      type: 'review',
      client: 'Finance Team'
    }
  ]);

  // Load meetings from localStorage and handle URL parameters
  useEffect(() => {
    const storedMeetings = JSON.parse(localStorage.getItem('teamsdayMeetings') || '[]');
    if (storedMeetings.length > 0) {
      const formattedMeetings = storedMeetings.map((meeting: any) => ({
        id: parseInt(meeting.id),
        title: meeting.title,
        date: new Date(meeting.date + 'T' + meeting.time),
        duration: parseInt(meeting.duration),
        type: meeting.type,
        client: meeting.client || 'Unknown'
      }));
      
      setEvents(prev => [...prev, ...formattedMeetings]);
    }

    // Handle URL parameters for event selection
    const eventId = searchParams.get('event');
    const eventDate = searchParams.get('date');
    
    if (eventId && eventDate) {
      const event = events.find(e => e.id.toString() === eventId);
      if (event) {
        setSelectedEvent(event);
        setSelectedDate(new Date(eventDate));
        setCurrentDate(new Date(eventDate));
      }
    }
  }, [searchParams]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getWeekDays = (date: Date) => {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day;
    startOfWeek.setDate(diff);

    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      weekDays.push(day);
    }
    return weekDays;
  };

  const getEventsForDate = (date: Date) => {
    return events.filter(event => 
      event.date.toDateString() === date.toDateString()
    );
  };

  const getEventsForHour = (date: Date, hour: number) => {
    return events.filter(event => 
      event.date.toDateString() === date.toDateString() &&
      event.date.getHours() === hour
    );
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'meeting': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'inspection': return 'bg-green-100 text-green-800 border-green-200';
      case 'review': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'presentation': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const navigateMonth = (direction: number) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  const navigateWeek = (direction: number) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const navigateDay = (direction: number) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const handleViewChange = (newView: 'month' | 'week' | 'day') => {
    setView(newView);
    if (newView === 'day' && !selectedDate) {
      setSelectedDate(new Date());
    }
  };

  const handleNavigation = (direction: number) => {
    switch (view) {
      case 'month':
        navigateMonth(direction);
        break;
      case 'week':
        navigateWeek(direction);
        break;
      case 'day':
        navigateDay(direction);
        break;
    }
  };

  const getViewTitle = () => {
    switch (view) {
      case 'month':
        return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
      case 'week':
        const weekStart = getWeekDays(currentDate)[0];
        const weekEnd = getWeekDays(currentDate)[6];
        if (weekStart.getMonth() === weekEnd.getMonth()) {
          return `${monthNames[weekStart.getMonth()]} ${weekStart.getDate()}-${weekEnd.getDate()}, ${weekStart.getFullYear()}`;
        } else {
          return `${monthNames[weekStart.getMonth()]} ${weekStart.getDate()} - ${monthNames[weekEnd.getMonth()]} ${weekEnd.getDate()}, ${weekStart.getFullYear()}`;
        }
      case 'day':
        return currentDate.toLocaleDateString('en-US', { 
          weekday: 'long',
          month: 'long', 
          day: 'numeric',
          year: 'numeric'
        });
    }
  };

  const renderMonthView = () => {
    const days = getDaysInMonth(currentDate);
    
    return (
      <div className="p-6">
        {/* Week Days Header */}
        <div className="grid grid-cols-7 gap-px mb-2">
          {weekDays.map((day) => (
            <div key={day} className="p-3 text-center text-sm font-medium text-gray-500">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
          {days.map((day, index) => {
            const dayEvents = day ? getEventsForDate(day) : [];
            const isToday = day && day.toDateString() === new Date().toDateString();
            const isSelected = day && selectedDate && day.toDateString() === selectedDate.toDateString();
            
            return (
              <div
                key={index}
                className={`bg-white min-h-[120px] p-2 cursor-pointer hover:bg-gray-50 ${
                  !day ? 'bg-gray-50' : ''
                } ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => day && setSelectedDate(day)}
              >
                {day && (
                  <>
                    <div className={`text-sm font-medium mb-1 ${
                      isToday ? 'bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center' : 'text-gray-900'
                    }`}>
                      {day.getDate()}
                    </div>
                    
                    <div className="space-y-1">
                      {dayEvents.slice(0, 2).map((event) => (
                        <div
                          key={event.id}
                          className={`text-xs p-1 rounded border ${getEventTypeColor(event.type)} truncate`}
                          title={`${event.title} - ${formatTime(event.date)}`}
                        >
                          {event.title}
                        </div>
                      ))}
                      {dayEvents.length > 2 && (
                        <div className="text-xs text-gray-500 font-medium">
                          +{dayEvents.length - 2} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDaysData = getWeekDays(currentDate);
    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div className="p-6">
        {/* Week Header */}
        <div className="grid grid-cols-8 gap-px mb-4">
          <div className="p-3"></div>
          {weekDaysData.map((day, index) => {
            const isToday = day.toDateString() === new Date().toDateString();
            return (
              <div key={index} className="p-3 text-center">
                <div className="text-sm font-medium text-gray-500">{weekDays[index]}</div>
                <div className={`text-lg font-semibold mt-1 ${
                  isToday ? 'bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center mx-auto' : 'text-gray-900'
                }`}>
                  {day.getDate()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Week Grid */}
        <div className="max-h-96 overflow-y-auto">
          <div className="grid grid-cols-8 gap-px bg-gray-200">
            {hours.map((hour) => (
              <div key={hour} className="contents">
                <div className="bg-white p-2 text-xs text-gray-500 text-right border-r">
                  {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                </div>
                {weekDaysData.map((day, dayIndex) => {
                  const hourEvents = getEventsForHour(day, hour);
                  return (
                    <div key={dayIndex} className="bg-white min-h-[60px] p-1 border-b border-gray-100 hover:bg-gray-50">
                      {hourEvents.map((event) => (
                        <div
                          key={event.id}
                          className={`text-xs p-1 rounded mb-1 ${getEventTypeColor(event.type)} truncate`}
                          title={`${event.title} - ${event.client}`}
                        >
                          {event.title}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const dayEvents = getEventsForDate(currentDate);

    return (
      <div className="p-6">
        <div className="max-h-96 overflow-y-auto">
          <div className="space-y-px">
            {hours.map((hour) => {
              const hourEvents = getEventsForHour(currentDate, hour);
              return (
                <div key={hour} className="flex bg-white border-b border-gray-100">
                  <div className="w-20 p-3 text-xs text-gray-500 text-right border-r">
                    {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                  </div>
                  <div className="flex-1 min-h-[60px] p-2 hover:bg-gray-50">
                    {hourEvents.map((event) => (
                      <div
                        key={event.id}
                        className={`p-2 rounded mb-2 ${getEventTypeColor(event.type)}`}
                      >
                        <div className="font-medium text-sm">{event.title}</div>
                        <div className="text-xs opacity-75">{event.client} • {event.duration} min</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Day Events Summary */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">Today's Schedule</h4>
          {dayEvents.length > 0 ? (
            <div className="space-y-2">
              {dayEvents.map((event) => (
                <div key={event.id} className="flex items-center justify-between p-2 bg-white rounded border">
                  <div>
                    <div className="font-medium text-sm">{event.title}</div>
                    <div className="text-xs text-gray-500">{event.client}</div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatTime(event.date)} ({event.duration} min)
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No events scheduled for this day</p>
          )}
        </div>
      </div>
    );
  };

  const renderCalendarContent = () => {
    switch (view) {
      case 'month':
        return renderMonthView();
      case 'week':
        return renderWeekView();
      case 'day':
        return renderDayView();
      default:
        return renderMonthView();
    }
  };

  const handleNewEvent = () => {
    setShowNewEventModal(true);
    if (selectedDate) {
      setNewEvent(prev => ({
        ...prev,
        date: selectedDate.toISOString().split('T')[0]
      }));
    }
  };

  const handleSaveEvent = () => {
    if (newEvent.title && newEvent.date && newEvent.time) {
      // Here you would typically save to a database
      console.log('Saving event:', newEvent);
      setShowNewEventModal(false);
      setNewEvent({
        title: '',
        date: '',
        time: '',
        duration: 60,
        type: 'meeting',
        client: '',
        description: ''
      });
    }
  };

  const handleCloseModal = () => {
    setShowNewEventModal(false);
    setNewEvent({
      title: '',
      date: '',
      time: '',
      duration: 60,
      type: 'meeting',
      client: '',
      description: ''
    });
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Calendar</h1>
              <p className="text-sm text-gray-600 mt-1">Manage your schedule and appointments</p>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* View Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                {['month', 'week', 'day'].map((viewType) => (
                  <button
                    key={viewType}
                    onClick={() => handleViewChange(viewType as 'month' | 'week' | 'day')}
                    className={`px-3 py-1 text-sm font-medium rounded-md capitalize whitespace-nowrap ${
                      view === viewType
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {viewType}
                  </button>
                ))}
              </div>
              
              <button 
                onClick={handleNewEvent}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center whitespace-nowrap"
              >
                <i className="ri-add-line mr-2"></i>
                New Event
              </button>
            </div>
          </div>
        </div>

        {/* Calendar Content */}
        <div className="flex-1 p-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full">
            {/* Calendar Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {getViewTitle()}
                </h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleNavigation(-1)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <i className="ri-arrow-left-line text-gray-600"></i>
                  </button>
                  <button
                    onClick={() => handleNavigation(1)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <i className="ri-arrow-right-line text-gray-600"></i>
                  </button>
                </div>
              </div>
              
              <button
                onClick={goToToday}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 whitespace-nowrap"
              >
                Today
              </button>
            </div>

            {/* Calendar Views */}
            {renderCalendarContent()}
          </div>
        </div>
      </div>

      {/* Event Details Sidebar */}
      {(selectedDate || selectedEvent) && (
        <div className="w-80 bg-white border-l border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {selectedEvent ? 'Event Details' : 
                selectedDate?.toLocaleDateString('en-US', { 
                  weekday: 'long',
                  month: 'long', 
                  day: 'numeric' 
                })
              }
            </h3>
            <button
              onClick={() => {
                setSelectedDate(null);
                setSelectedEvent(null);
              }}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <i className="ri-close-line text-gray-500"></i>
            </button>
          </div>

          <div className="space-y-4">
            {selectedEvent ? (
              // Show selected event details
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{selectedEvent.title}</h4>
                  <span className={`text-xs px-2 py-1 rounded-full ${getEventTypeColor(selectedEvent.type)}`}>
                    {selectedEvent.type}
                  </span>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex items-center">
                    <i className="ri-time-line mr-2"></i>
                    {formatTime(selectedEvent.date)} ({selectedEvent.duration} min)
                  </div>
                  <div className="flex items-center">
                    <i className="ri-user-line mr-2"></i>
                    {selectedEvent.client}
                  </div>
                  <div className="flex items-center">
                    <i className="ri-calendar-line mr-2"></i>
                    {selectedEvent.date.toLocaleDateString()}
                  </div>
                </div>
                <div className="flex space-x-2 mt-3">
                  <button className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 whitespace-nowrap">
                    Edit
                  </button>
                  <button className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200 whitespace-nowrap">
                    Delete
                  </button>
                </div>
              </div>
            ) : selectedDate ? (
              // Show events for selected date
              getEventsForDate(selectedDate).length > 0 ? (
                getEventsForDate(selectedDate).map((event) => (
                  <div 
                    key={event.id} 
                    className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => setSelectedEvent(event)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{event.title}</h4>
                      <span className={`text-xs px-2 py-1 rounded-full ${getEventTypeColor(event.type)}`}>
                        {event.type}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div className="flex items-center">
                        <i className="ri-time-line mr-2"></i>
                        {formatTime(event.date)} ({event.duration} min)
                      </div>
                      <div className="flex items-center">
                        <i className="ri-user-line mr-2"></i>
                        {event.client}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <i className="ri-calendar-line text-4xl text-gray-300 mb-2"></i>
                  <p className="text-gray-500">No events scheduled</p>
                  <button 
                    onClick={handleNewEvent}
                    className="mt-2 text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded hover:bg-blue-200 whitespace-nowrap"
                  >
                    Add Event
                  </button>
                </div>
              )
            ) : null}
          </div>
        </div>
      )}

      {/* New Event Modal */}
      {showNewEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">New Event</h3>
              <button
                onClick={handleCloseModal}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Title *
                </label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter event title"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time *
                  </label>
                  <input
                    type="time"
                    value={newEvent.time}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    value={newEvent.duration}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, duration: parseInt(e.target.value) || 60 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="15"
                    step="15"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Event Type
                  </label>
                  <select
                    value={newEvent.type}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="meeting">Meeting</option>
                    <option value="inspection">Inspection</option>
                    <option value="review">Review</option>
                    <option value="presentation">Presentation</option>
                    <option value="deadline">Deadline</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client/Company
                </label>
                <input
                  type="text"
                  value={newEvent.client}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, client: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter client or company name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter event description (optional)"
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEvent}
                disabled={!newEvent.title || !newEvent.date || !newEvent.time}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Create Event
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
